import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Envelope } from '../../interfaces/envelope';
import { Event, EventTypes } from '../../interfaces/events';
import { eventsService } from '../services/events.service';
import { EventsParam } from '../../api/events';

export type EventObjects = {
	dt_ambient_temperature_C?: Event[];
	dt_relative_humidity_pct?: Event[];
	dt_co2_ppm?: Event[];
};

interface EventsState {
	events: {
		[id: number]: EventObjects;
	};
	lastEvents: {
		[id: number]: EventObjects;
	};
	isLoading: boolean;
}

const initialState = {
	events: [],
	lastEvents: [],
	isLoading: false,
} as EventsState;

export const fetchEvents = createAsyncThunk<
	{ events: Event[]; devices: string[]; types: EventTypes[] },
	{ param: EventsParam },
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('events/fetchEvents', async ({ param }, { rejectWithValue }) => {
	try {
		const events = await eventsService.fetchEvents(param);
		return {
			events: events,
			devices: param.fk_devices,
			types: param.fk_event_types as EventTypes[],
		};
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const fetchEventsLast = createAsyncThunk<
	{ events: Event[]; devices: string[]; types: EventTypes[] },
	{ param: EventsParam },
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('events/fetchEventsLast', async ({ param }, { rejectWithValue }) => {
	try {
		const events = await eventsService.fetchEventsLast(param);
		return {
			events: events,
			devices: param.fk_devices,
			types: param.fk_event_types as EventTypes[],
		};
	} catch (error) {
		return rejectWithValue(error);
	}
});

const eventsSlice = createSlice({
	name: 'events',
	initialState,
	reducers: {
		removeEvent: (state, action: PayloadAction<number>) => {
			delete state.events[action.payload];
		},
		resetEvents: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchEvents.fulfilled, (state, action) => {
			for (const device of action.payload.devices) {
				const deviceEvents: Event[] = action.payload.events.filter(
					(event: Event) => event.fk_device === Number(device)
				);

				let eventObjects: EventObjects = {};

				for (const eventType of action.payload.types) {
					eventObjects[eventType] = deviceEvents.filter(
						(event) => event.fk_event_type === eventType
					);
				}

				state.events = {
					...state.events,
					[Number(device)]: { ...state.events[Number(device)], ...eventObjects },
				};
			}
			state.isLoading = false;
		});
		builder.addCase(fetchEvents.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchEvents.rejected, (state, action) => {
			state.events = [];
			state.isLoading = false;
		});

		builder.addCase(fetchEventsLast.fulfilled, (state, action) => {
			for (const device of action.payload.devices) {
				const deviceEvents: Event[] = action.payload.events.filter(
					(event: Event) => event.fk_device === Number(device)
				);

				let eventObjects: EventObjects = {};

				for (const eventType of action.payload.types) {
					eventObjects[eventType] = deviceEvents.filter(
						(event) => event.fk_event_type === eventType
					);
				}

				state.lastEvents = {
					...state.lastEvents,
					[Number(device)]: { ...state.lastEvents[Number(device)], ...eventObjects },
				};
			}
			state.isLoading = false;
		});
		builder.addCase(fetchEventsLast.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchEventsLast.rejected, (state, action) => {
			state.lastEvents = [];
			state.isLoading = false;
		});
	},
});

export const { removeEvent, resetEvents } = eventsSlice.actions;

export default eventsSlice;
